import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbar } from '@angular/material/toolbar';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, forkJoin, map, of } from 'rxjs';
import { Pop } from 'src/app/classes/shared/pop';
import { TranslateService } from '@ngx-translate/core';
import { TrackerConfigTool } from 'src/app/classes/shared/tracker-config.tool';
import { TrackerConfig } from 'src/app/interfaces/api/trackerConfig';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { ButtonComponent } from 'ui-library';
import { TrackerConfigFormComponent } from 'src/app/components/project-settings/resources/tracker-config-form/tracker-config-form.component';

// "Même modification pour tous" mode of tracker-configs.component.ts's bulkEditSelection() -
// counterpart to KydBulkConfigDialogComponent, but overwriting the settings/offlineDelay of
// several existing TrackerConfig templates at once instead of the settings of several
// trackers. Each template keeps its own id/name/type untouched - only the shared form
// below (one type, since the caller only offers this for a uniform-type selection) replaces
// every selected template's settings and offlineDelay.
@Component({
  selector: 'app-tracker-config-bulk-edit-dialog',
  imports: [
    CommonModule,
    TranslateModule,
    MatDialogModule,
    MatIconModule,
    MatToolbar,
    ButtonComponent,
    TrackerConfigFormComponent,
  ],
  standalone: true,
  templateUrl: './tracker-config-bulk-edit-dialog.component.html',
  styleUrls: ['./tracker-config-bulk-edit-dialog.component.scss']
})
export class TrackerConfigBulkEditDialogComponent {
  private readonly trackerService: TrackerService = inject(TrackerService);
  private readonly trackerConfigTool: TrackerConfigTool = inject(TrackerConfigTool);
  private readonly pop: Pop = inject(Pop);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly data: { configs: TrackerConfig[] } = inject(MAT_DIALOG_DATA);
  readonly dialogRef: MatDialogRef<TrackerConfigBulkEditDialogComponent, boolean> = inject(MatDialogRef<TrackerConfigBulkEditDialogComponent, boolean>);

  readonly configs: TrackerConfig[] = this.data.configs;
  // The caller (tracker-configs.component.ts's bulkEditSelection()) only offers this dialog for
  // a uniform-type selection, so any template's type is representative of the whole batch.
  readonly type: NonNullable<TrackerConfig['type']> = this.configs[0]?.type ?? 'Unit';
  // No single template's current values make sense to preload here, same reasoning as
  // KydBulkConfigDialogComponent's own configDraft - starts from sensible defaults instead.
  configDraft: TrackerConfig = { id: '', name: '', type: this.type, settings: this.trackerConfigTool.mapNestedToFlat(null, this.type) };
  saving: boolean = false;

  save() {
    this.saving = true;

    const applies = this.configs.map(config =>
      this.trackerService.setConfig({ ...config, settings: this.configDraft.settings, offlineDelay: this.configDraft.offlineDelay } as any).pipe(
        map(() => true),
        catchError(err => { console.error('[TrackerConfigs bulk edit] failed for', config.name, err); return of(false); })
      )
    );

    forkJoin(applies).subscribe(results => {
      this.saving = false;
      const failedCount = results.filter(r => !r).length;

      if (failedCount === 0) {
        this.pop.toast(this.translate.instant('menu.devices.configs.bulk_edit_saved', { count: results.length }), 'success', 4000, 'save');
      } else if (failedCount < results.length) {
        this.pop.warning(this.translate.instant('trackers.error.some_kyd_config_failed', { failed: failedCount, total: results.length }));
      } else {
        this.pop.error(this.translate.instant('trackers.error.config_save'));
      }
      this.dialogRef.close(true);
    });
  }

  cancel() {
    this.dialogRef.close(false);
  }
}
